import { motion } from "motion/react";
import { AlarmClock, BellRing, CalendarClock, CalendarRange, CircleCheck, Clock4, Eye, Hand, Link2, ListChecks, Play, RotateCcw, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { directionLabel, isMyStaging, ME_HEAD_OF, PEOPLE } from "../../data/people";
import { useAccountInfo } from "../../lib/accounts";
import { itemsToApprove, itemsToModerate, itemsToPublish, lastVersion, liveItems, needsCheck, tasksLabel } from "../../lib/domain";
import { spring } from "../../lib/motion";
import { ago, dayInfo, dayOf, dayOn, timeOf } from "../../lib/time";
import { cn, formatDuration, plural } from "../../lib/utils";
import type { Task } from "../../types";
import { Avatar } from "../../components/ui/primitives";
import { PublicAvatar } from "../../components/PublicAccount";

function StatePill({ icon, children, className }: { icon: ReactNode; children: ReactNode; className: string }) {
  return (
    <div className={cn("flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium [&_svg]:size-[13px] [&_svg]:shrink-0", className)}>
      {icon}
      <span className="truncate">{children}</span>
    </div>
  );
}

function Top({ task, now, mine, danger }: { task: Task; now: number; mine: boolean; danger?: boolean }) {
  const soon = danger || (task.status !== "done" && task.due - now < 150 && task.due > now - 600);
  return (
    <div className="flex items-center gap-1.5">
      {mine && <span className="inline-flex h-[22px] shrink-0 items-center rounded-full bg-primary px-2 text-[11px] font-semibold text-primary-foreground">Моя</span>}
      <span className="flex-1" />
      {soon ? <AlarmClock className="size-[13px] text-rework-fg" /> : <Clock4 className="size-[13px] text-muted-foreground" />}
      <span className={cn("whitespace-nowrap text-xs font-medium", soon ? "text-rework-fg" : "text-muted-foreground")}>
        {dayOf(task.due) === dayOf(now)
          ? `до ${timeOf(task.due)}`
          : dayOf(task.due) === dayOf(now) + 1
            ? `Завтра ${timeOf(task.due)}`
            : `${dayInfo(dayOf(task.due)).abbr} ${timeOf(task.due)}`}
      </span>
    </div>
  );
}

function Who({ task }: { task: Task }) {
  if (!task.editor) {
    return (
      <div className="flex items-center gap-2 text-[13px] font-medium text-muted-foreground">
        <Hand className="size-4" />
        <span className="flex-1 truncate">Свободная — ждёт исполнителя</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Avatar id={task.editor} size={22} />
      <span className="flex-1 truncate text-[13px] font-medium">{PEOPLE[task.editor].name}</span>
    </div>
  );
}

/**
 * Карточка доски. У модератора та же доска отдела, но «своё» для него —
 * сданные ролики, которые он ещё не смотрел: они белые и с плашкой
 * «Проверить», остальное приглушено.
 */
export function TaskCard({ task, now, me, moderator, onOpen }: { task: Task; now: number; me: string; moderator?: boolean; onOpen: () => void }) {
  const fresh = (task.freshUntil ?? 0) > Date.now();
  // Куда выходит ролик: у публикации один аккаунт (08.10.2026).
  const account = useAccountInfo(liveItems(task).length === 1 ? liveItems(task)[0].accountId : null);
  const where = [directionLabel(task), account?.name].filter(Boolean).join(" · ");
  const check = !!moderator && needsCheck(task);
  // «Моя» — тема, по которой решаю я: принимаю видео, назначаю, переношу.
  const own = !moderator && (task.creator === me || isMyStaging(task));
  const mine = moderator ? check : own;
  const count = liveItems(task).length;
  const yourTurn = moderator ? check : own && (task.status === "review" || !!task.proposal);
  const modPill = check ? (
    <StatePill icon={<Eye />} className="bg-review-soft text-review-fg">
      Проверить
    </StatePill>
  ) : moderator && task.items.some((i) => i.mod?.verdict === "ok") ? (
    <StatePill icon={<ShieldCheck />} className="bg-done-soft text-done-fg">
      Норм
    </StatePill>
  ) : null;

  if (task.status === "done") {
    return (
      <motion.button
        type="button"
        layoutId={`task-${task.id}`}
        layout
        transition={spring.gentle}
        onClick={onOpen}
        style={{ borderRadius: 20 }}
        className={cn("flex w-full flex-col gap-2 px-3.5 py-3 text-left transition-[background-color,box-shadow]", mine ? "bg-card ring-1 ring-inset ring-border hover:ring-foreground/20" : "bg-card/55 text-foreground/75 hover:bg-card")}
      >
        <span className="text-[13px] font-medium leading-snug">
          {where && <span className="mb-0.5 flex min-w-0 items-center gap-1.5 text-[11px] font-medium tracking-wide text-muted-foreground">{account && <PublicAvatar account={account} className="size-5" />}<span className="truncate">{where}</span></span>}
          {task.title}
        </span>
        <span className="flex items-center gap-1.5">
          <Avatar id={task.creator} size={18} />
          <span className="flex-1 truncate text-xs text-muted-foreground">
            {task.approvedAt ? (dayOf(task.approvedAt) === dayOf(now) ? timeOf(task.approvedAt) : ago(task.approvedAt, now)) : ""}
          </span>
          {check ? <Eye className="size-3.5 text-review-fg" /> : <CircleCheck className="size-3.5 text-done" />}
        </span>
        {check && modPill}
      </motion.button>
    );
  }

  let body: ReactNode = null;
  if (task.status === "open") {
    body = <Who task={task} />;
  } else if (task.status === "wait") {
    const waited = now - task.createdAt;
    body = (
      <>
        <Who task={task} />
        {task.proposal ? (
          <StatePill icon={<CalendarClock />} className="bg-review-soft text-review-fg">
            Предлагает {dayOn(task.proposal.day, now)}, до {timeOf(task.proposal.due)}
          </StatePill>
        ) : (
          waited > 60 && (
            <StatePill icon={<BellRing />} className="bg-rework-soft text-rework-fg">
              Не взял уже час
            </StatePill>
          )
        )}
      </>
    );
  } else if (task.status === "work") {
    body = (
      <>
        <Who task={task} />
        {task.day > dayOf(now) && (
          <StatePill icon={<CalendarClock />} className="bg-work-soft text-work-fg">
            Начнёт {dayOn(task.day, now)}
          </StatePill>
        )}
      </>
    );
  } else if (task.status === "review") {
    // Показываем тот ролик, которого ждут прямо сейчас: у модератора — не
    // смотренный им, у постановщика — прошедший модератора.
    const queue = moderator ? itemsToModerate(task) : itemsToApprove(task);
    const item = queue[0] ?? task.items.find((i) => i.versions.length) ?? task.items[0];
    const ver = item && lastVersion(item);
    const waiting = queue.length;
    body = (
      <>
        <div className="flex items-center gap-2.5">
          <span className="relative grid h-[54px] w-9 shrink-0 place-items-center overflow-hidden rounded-[10px] bg-player">
            <img src={ver?.poster ?? item?.poster ?? task.poster} alt="" className="absolute inset-0 size-full object-cover" draggable={false} />
            <Play className="relative size-3 fill-white text-white" />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-[13px] font-semibold">
              {waiting > 1 ? `${waiting} ${plural(waiting, ["ролик ждёт", "ролика ждут", "роликов ждут"])}` : `v${ver?.n} · ${formatDuration(ver?.duration ?? 0)}`}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Avatar id={task.editor} size={18} />
              <span className="truncate">{PEOPLE[task.editor].name}</span>
            </span>
          </div>
        </div>
        {own && (
          <StatePill icon={<Eye />} className="bg-review-soft text-review-fg">
            Ваш ход
          </StatePill>
        )}
        {modPill}
      </>
    );
  } else if (task.status === "rework") {
    const cnt = task.items.reduce((n, i) => n + (i.status === "rework" ? i.revisions.filter((r) => r.round === i.round - 1).length : 0), 0);
    body = (
      <>
        <Who task={task} />
        <StatePill icon={<RotateCcw />} className="bg-rework-soft text-rework-fg">
          {cnt} {plural(cnt, ["правка", "правки", "правок"])}
        </StatePill>
        {check && modPill}
      </>
    );
  } else if (task.status === "publish") {
    const waiting = itemsToPublish(task).length;
    body = (
      <>
        <Who task={task} />
        <StatePill icon={<Link2 />} className="bg-publish-soft text-publish-fg">
          {own ? "Приложите ссылки" : "Ждёт ссылок"} · {waiting}
        </StatePill>
      </>
    );
  }

  return (
    <motion.button
      type="button"
      layoutId={`task-${task.id}`}
      layout
      initial={fresh ? { opacity: 0, y: -16, scale: 0.96 } : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={spring.gentle}
      onClick={onOpen}
      style={{ borderRadius: 20 }}
      className={cn(
        "flex w-full flex-col gap-2.5 p-3.5 text-left transition-[background-color,box-shadow]",
        // Плоские, без теней (08.10.2026): свои — белые с тонкой рамкой, чужие — приглушённые
        mine ? "bg-card ring-1 ring-inset ring-border hover:ring-foreground/20" : "bg-card/55 text-foreground/75 hover:bg-card",
        yourTurn && "ring-2 ring-inset ring-[rgb(139_92_246/0.45)]",
        fresh && "glow-new",
      )}
    >
      {/* У руководителя своя вся доска — «Моя» стояла на каждой карточке и
          ничего не говорила (08.10.2026), поэтому ему метку не показываем. */}
      <Top task={task} now={now} mine={own && !ME_HEAD_OF.includes(task.divisionId)} />
      <span className="flex flex-col gap-1">
        <span className="text-sm font-semibold leading-[1.35]">
          {where && <span className="mb-0.5 flex min-w-0 items-center gap-1.5 text-[11px] font-medium tracking-wide text-muted-foreground">{account && <PublicAvatar account={account} className="size-5" />}<span className="truncate">{where}</span></span>}
          {task.title}
        </span>
        {task.period === "week" && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarRange className="size-3.5" />
            публикация на неделю
          </span>
        )}
        {count > 1 && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ListChecks className="size-3.5" />
            {tasksLabel(count)}
          </span>
        )}
      </span>
      {body}
    </motion.button>
  );
}
